/**
 * Assistant page - Asistente normativo RAG
 */

import { useState } from 'react';
import { askQuestion, type AskResponse } from '../services/rag';
import '../styles/Assistant.css';

export default function Assistant() {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [response, setResponse] = useState<AskResponse | null>(null);
  const [history, setHistory] = useState<Array<{ question: string; response: AskResponse }>>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!question.trim()) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const answer = await askQuestion({
        question: question.trim(),
      });

      setResponse(answer);
      setHistory([...history, { question: question.trim(), response: answer }]);
      setQuestion('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al procesar la consulta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="assistant">
      <div className="page-header">
        <h1>Asistente Normativo</h1>
        <p className="subtitle">Consulta el REBT/ITC y obtén respuestas verificables</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="assistant-container">
        <div className="chat-area">
          {/* History */}
          {history.length > 0 && (
            <div className="chat-history">
              {history.map((item, index) => (
                <div key={index} className="history-item">
                  <div className="question-bubble">
                    <strong>Pregunta:</strong> {item.question}
                  </div>
                  <div className="answer-bubble">
                    <div className="answer-content">
                      {item.response.answer}
                    </div>
                    {item.response.citations.length > 0 && (
                      <div className="citations">
                        <strong>Referencias:</strong>
                        <ul>
                          {item.response.citations.map((citation: any, idx: number) => (
                            <li key={idx}>
                              {citation.itc_reference || 'REBT'}
                              {citation.section && ` - ${citation.section}`}
                              {citation.page && ` (página ${citation.page})`}
                              <span className="relevance">
                                {' '}(relevancia: {((citation.relevance_score || 0) * 100).toFixed(1)}%)
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {item.response.recommended_evidence && item.response.recommended_evidence.length > 0 && (
                      <div className="recommended-evidence">
                        <strong>Evidencias recomendadas:</strong>{' '}
                        {item.response.recommended_evidence.join(', ')}
                      </div>
                    )}
                    {item.response.practical_implication && (
                      <div className="practical-implication">
                        <strong>Implicación práctica:</strong>{' '}
                        {item.response.practical_implication}
                      </div>
                    )}
                    {!item.response.has_sufficient_support && (
                      <div className="warning">
                        ⚠️ La respuesta puede no tener suficiente soporte en el reglamento. Consulta directamente el REBT/ITC para mayor certeza.
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Current response */}
          {response && !history.find(item => item.response === response) && (
            <div className="current-response">
              <div className="answer-bubble">
                <div className="answer-content">
                  {response.answer}
                </div>
                {response.citations.length > 0 && (
                  <div className="citations">
                    <strong>Referencias:</strong>
                    <ul>
                      {response.citations.map((citation: any, idx: number) => (
                        <li key={idx}>
                          {citation.itc_reference || 'REBT'}
                          {citation.section && ` - ${citation.section}`}
                          {citation.page && ` (página ${citation.page})`}
                          <span className="relevance">
                            {' '}(relevancia: {((citation.relevance_score || 0) * 100).toFixed(1)}%)
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {response.recommended_evidence && response.recommended_evidence.length > 0 && (
                  <div className="recommended-evidence">
                    <strong>Evidencias recomendadas:</strong>{' '}
                    {response.recommended_evidence.join(', ')}
                  </div>
                )}
                {response.practical_implication && (
                  <div className="practical-implication">
                    <strong>Implicación práctica:</strong>{' '}
                    {response.practical_implication}
                  </div>
                )}
                {!response.has_sufficient_support && (
                  <div className="warning">
                    ⚠️ La respuesta puede no tener suficiente soporte en el reglamento. Consulta directamente el REBT/ITC para mayor certeza.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Input form */}
          <form onSubmit={handleSubmit} className="input-form">
            <div className="input-container">
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Pregunta sobre el REBT/ITC... (ej: ¿Qué requiere la ITC-BT-18 para puesta a tierra?)"
                rows={3}
                disabled={loading}
                className="question-input"
              />
              <button
                type="submit"
                disabled={loading || !question.trim()}
                className="btn-primary send-button"
              >
                {loading ? 'Consultando...' : 'Enviar'}
              </button>
            </div>
          </form>
        </div>

        <div className="sidebar">
          <div className="info-card">
            <h3>¿Cómo funciona?</h3>
            <p>
              El asistente normativo utiliza inteligencia artificial para buscar
              información en el Reglamento Electrotécnico para Baja Tensión (REBT)
              y sus Instrucciones Técnicas Complementarias (ITC).
            </p>
            <ul>
              <li>Responde solo con información verificable del reglamento</li>
              <li>Incluye citas específicas (ITC, sección, página)</li>
              <li>No inventa requisitos normativos</li>
              <li>Sugiere evidencias recomendadas cuando aplica</li>
            </ul>
          </div>

          <div className="examples-card">
            <h3>Ejemplos de preguntas</h3>
            <ul>
              <li>"¿Qué requiere la ITC-BT-18 para puesta a tierra?"</li>
              <li>"¿Cuál es el valor mínimo de resistencia de aislamiento según ITC-BT-19?"</li>
              <li>"¿Qué verificaciones son obligatorias para una instalación industrial?"</li>
              <li>"¿Qué evidencias necesito para validar la puesta a tierra?"</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
